import type { Edge } from "@cimara/sim";
import type { World } from "@cimara/world";

/** Lateral offset of one lane unit in metres. */
export const LANE_METERS = 1.15;

export interface PathSample {
  edgeId: number;
  s: number;
  x: number;
  y: number;
  z: number;
  /** Radians, 0 = facing +Z. */
  heading: number;
  lateralX: number;
  lateralZ: number;
  width: number;
}

export interface Pose {
  x: number;
  y: number;
  z: number;
  heading: number;
}

export interface AdvanceResult {
  edgeId: number;
  s: number;
}

export interface RouteIndex {
  readonly routeId: number | null;
  readonly lengthM: number;
  /** Route-relative distance at the start of an edge. */
  distanceAt(edgeId: number, s: number): number | null;
  edgeStart(edgeId: number): number | null;
  edges(): Edge[];
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/**
 * One centreline sample with a tangent and a horizontal lateral vector. The
 * lateral points left when looking along the forward tangent, matching
 * `buildRoadGeometry` in `@cimara/world`.
 */
export function samplePath(world: World, edgeId: number, s: number): PathSample | null {
  const edge = world.getEdge(edgeId);
  if (!edge) return null;

  const clampedS = clamp(s, 0, edge.length);
  const position = world.samplePosition(edgeId, clampedS);
  const behind = world.samplePosition(edgeId, clamp(clampedS - 1.5, 0, edge.length));
  const ahead = world.samplePosition(edgeId, clamp(clampedS + 1.5, 0, edge.length));

  let dx = ahead.x - behind.x;
  let dz = ahead.z - behind.z;
  let length = Math.hypot(dx, dz);
  if (length < 1e-6) {
    dx = 0;
    dz = 1;
    length = 1;
  }
  dx /= length;
  dz /= length;

  return {
    edgeId,
    s: clampedS,
    x: position.x,
    y: position.y,
    z: position.z,
    heading: Math.atan2(dx, dz),
    lateralX: -dz,
    lateralZ: dx,
    width: edge.width,
  };
}

/** Position a rider in the centre of `lane` (lane 0 is the centreline). */
export function samplePose(
  world: World,
  edgeId: number,
  s: number,
  lane: number,
): Pose | null {
  const sample = samplePath(world, edgeId, s);
  if (!sample) return null;
  const laneOffset = (Number.isFinite(lane) ? lane : 0) * LANE_METERS;
  return {
    x: sample.x + sample.lateralX * laneOffset,
    y: sample.y,
    z: sample.z + sample.lateralZ * laneOffset,
    heading: sample.heading,
  };
}

/**
 * Move forward along the graph by `meters`. This is the local extrapolation
 * used between authoritative snapshots; the server still owns the real state.
 */
export function advanceRoute(
  world: World,
  edgeId: number,
  s: number,
  meters: number,
): AdvanceResult {
  const start = world.getEdge(edgeId);
  if (!start) return { edgeId, s: Math.max(0, s) };
  if (!Number.isFinite(meters) || meters <= 0) {
    return { edgeId, s: clamp(s, 0, start.length) };
  }

  let currentEdge = edgeId;
  let currentS = clamp(s, 0, start.length);
  let remaining = meters;

  for (let guard = 0; guard < 64 && remaining > 0; guard += 1) {
    const edge = world.getEdge(currentEdge);
    if (!edge) break;
    const left = edge.length - currentS;
    if (remaining <= left) {
      currentS += remaining;
      remaining = 0;
      break;
    }

    remaining -= left;
    const next = world.nextEdge(currentEdge, edge.length);
    if (next === null) {
      currentS = edge.length;
      break;
    }
    if (next === currentEdge) break;
    currentEdge = next;
    currentS = 0;
  }

  return { edgeId: currentEdge, s: currentS };
}

export function createRouteIndex(world: World, routeId?: number): RouteIndex {
  const route =
    (routeId === undefined ? undefined : world.getRoute(routeId)) ??
    world.routes[0] ??
    null;

  const starts = new Map<number, number>();
  const routeEdges: Edge[] = [];
  let length = 0;

  if (route) {
    for (const edgeId of route.edgeIds) {
      const edge = world.getEdge(edgeId);
      if (!edge) continue;
      if (!starts.has(edgeId)) {
        starts.set(edgeId, length);
        length += edge.length;
        routeEdges.push(edge);
      }
    }
  }

  return {
    routeId: route?.id ?? null,
    lengthM: length,
    distanceAt(edgeId: number, s: number): number | null {
      const start = starts.get(edgeId);
      if (start === undefined) return null;
      const edgeLength = world.edgeLength(edgeId);
      return start + clamp(s, 0, edgeLength);
    },
    edgeStart(edgeId: number): number | null {
      return starts.get(edgeId) ?? null;
    },
    edges(): Edge[] {
      return routeEdges;
    },
  };
}

/**
 * Detect a forward route wrap. Returns 1 on the frame the rider crosses the
 * start/finish line, -1 if a correction moves backwards over it, else 0.
 */
export function lapDelta(previousOffset: number, nextOffset: number, routeLength: number): number {
  if (!(routeLength > 0)) return 0;
  const half = routeLength / 2;
  if (nextOffset < previousOffset - half) return 1;
  if (nextOffset > previousOffset + half) return -1;
  return 0;
}
